import { InputAdornment, TextField, Tooltip } from '@mui/material'
import clsx from 'clsx'
import { KeyboardEventHandler, ReactNode, useState } from 'react'

import style from './TextFieldDescribed.module.css'

const TextFieldDescribed = ({
  description = undefined,
  error = '',
  hasError = false,
  defaultValue,
  placeholder,
  disabled = false,
  rightLabel,
  type = 'text',
  className,
  onChange,
  children,
  value,
  noBorder = false,
  textAlign = 'left',
  small = false,
  labelClassName,
  errorMode = 'text',
  autoComplete,
  dataTestId,
  id,
  name,
  onKeyDown,
}: {
  description: string | undefined
  error?: string
  hasError?: boolean
  defaultValue?: string | number
  value?: string | number
  placeholder: string
  disabled?: boolean
  type?: string
  rightLabel?: string
  className?: string
  onChange: (e: any) => void
  children?: ReactNode
  noBorder?: boolean
  textAlign?: 'left' | 'right' | 'center'
  small?: boolean
  labelClassName?: string
  /** `text` — message under the field; `tooltip` — red border + tooltip while invalid & focused. */
  errorMode?: 'text' | 'tooltip'
  autoComplete?: string
  dataTestId?: string
  id?: string
  name?: string
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
}) => {
  const [focused, setFocused] = useState(false)
  const [hovered, setHovered] = useState(false)
  const showTooltip = errorMode === 'tooltip' && hasError && Boolean(error)
  const errorBorder = hasError ? 'solid 1px var(--color-error-dark)' : 'var(--form-border)'
  const tooltipOpen = showTooltip && (focused || hovered)

  const field = (
    <TextField
      variant="outlined"
      placeholder={placeholder}
      fullWidth
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      disabled={disabled}
      type={type}
      id={id}
      name={name}
      onKeyDown={onKeyDown}
      sx={{
        '& fieldset': {
          borderRadius: 'var(--radius-lg)',
          border: noBorder ? 'none' : errorBorder,
        },
        '& .MuiInputBase-root': {
          height: small ? '32px' : '36px',
          fontSize: small ? '13px' : '14px',
          backgroundColor: 'var(--glass-bg)',
          color: 'var(--text-main)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          boxSizing: 'border-box',
        },
        '& .MuiInputBase-input': {
          height: '100%',
          boxSizing: 'border-box',
          paddingTop: 0,
          paddingBottom: 0,
        },
        '& .MuiInputBase-input:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 100px var(--glass-bg) inset',
          WebkitTextFillColor: 'var(--text-main)',
          caretColor: 'var(--text-main)',
          borderRadius: 'inherit',
          height: '100%',
          boxSizing: 'border-box',
          transition: 'background-color 99999s ease-out 0s',
        },
        '& .MuiInputBase-input:-webkit-autofill:hover, & .MuiInputBase-input:-webkit-autofill:focus, & .MuiInputBase-input:-webkit-autofill:active':
          {
            WebkitBoxShadow: '0 0 0 100px var(--glass-bg) inset',
            WebkitTextFillColor: 'var(--text-main)',
          },
        '& .MuiOutlinedInput-notchedOutline': {
          border: noBorder ? 'none' : errorBorder,
        },
        // Keep error border visible while typing — do not override with focus/hover info color.
        ...(hasError
          ? {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-error-dark) !important',
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-error-dark) !important',
              },
            }
          : {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-info) !important',
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-info) !important',
              },
            }),
      }}
      InputProps={{
        endAdornment: children ? <InputAdornment position="end">{children}</InputAdornment> : null,
      }}
      inputProps={{
        style: { textAlign },
        autoComplete,
        'data-test-id': dataTestId,
      }}
    />
  )

  return (
    <div className={className}>
      {description ? (
        <div className={clsx('text-14-400', style.subtitle, labelClassName)}>{description}</div>
      ) : null}
      <div className={style.flexRow}>
        {errorMode === 'tooltip' ? (
          <Tooltip
            title={showTooltip ? error : ''}
            arrow
            placement="bottom-start"
            open={tooltipOpen}
            disableInteractive
            disableHoverListener
            disableFocusListener
            disableTouchListener
          >
            <div
              className={style.tooltipFieldWrap}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
            >
              {field}
            </div>
          </Tooltip>
        ) : (
          field
        )}
        {rightLabel && <div className={style.rightLabel}>{rightLabel}</div>}
      </div>
      {errorMode === 'text' && hasError ? <div className={style.error}>{error}</div> : null}
    </div>
  )
}

export default TextFieldDescribed
